import { useCallback } from 'react';
import { type ExportParams } from 'lib/hooks/useServerExport';
import {
  postSpatialExport,
  useSpatialExport,
} from 'lib/hooks/use-spatial-export';

/**
 * The combined spatial-export request body: formats plus per-dataset filter params + attributes.
 *
 * Intersected with `ExportParams` so it carries that type's `[key: string]: unknown` index signature
 * and is assignable to `startExport`'s parameter both ways (a plain object type is not — that mismatch
 * was failing the `types` CI job).
 */
export type SpatialExportRequest = ExportParams & {
  formats: string[];
  datasets: Record<
    string,
    { params: Record<string, unknown>; attributes: string[] }
  >;
};

/**
 * Owns the Map Query combined spatial (GIS) export: POSTs the selected datasets + formats to the
 * combined endpoint and tracks completion over Pusher (auto-downloading the ZIP), through the shared
 * useSpatialExport flow. One export spans every selected dataset.
 *
 * @return {object} The server-export status + `startExport(request)` + the signed-in user id.
 */
export const useMapQuerySpatialExport = () => {
  const exportFn = useCallback(
    (params?: ExportParams) =>
      postSpatialExport('queries/map_query/spatial_export', params),
    []
  );

  return useSpatialExport<SpatialExportRequest>({
    event: 'map_query_export_complete',
    exportFn,
  });
};

export type MapQuerySpatialExport = ReturnType<typeof useMapQuerySpatialExport>;
